import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import GET_PRODUCT_FEATURES from "src/graphql/query/productandpricing/getProductFeatures";

// ---------- TYPES ----------
type PlanType = "freetrial" | "basic" | "standard" | "business" | "enterprise";

type AllowedPlans = {
  [key in PlanType]: 0 | 1;
};

interface Feature {
  id: string;
  name: string;
  description: string;
  allowed: AllowedPlans;
}

interface FeatureCategory {
  category: string;
  features: Feature[];
}

// ---------- HELPERS ----------
function buildFeatureMap(data: FeatureCategory[]) {
  return data
    .flatMap((cat) => cat.features)
    .reduce(
      (acc, f) => {
        acc[f.id] = f;
        return acc;
      },
      {} as Record<string, Feature>,
    );
}

function canUseFeature(
  featureMap: Record<string, Feature>,
  plan: PlanType,
  featureId: string,
): boolean | string {
  // return !!featureMap[featureId]?.allowed?.[plan];
  // ✅ If plan is freetrial → allow all features
  if (plan === "freetrial") return true;

  const value = featureMap[featureId]?.allowed?.[plan];

  // if value is undefined/null → not available
  if (value === undefined || value === null) return false;
  // if it's numeric or boolean-like (0/1) → return boolean
  if (typeof value === "number") return !!value;
  // if it's text (like "60 days" or "All time") → return as-is
  if (typeof value === "string") return value;
  // default fallback
  return false;
}

// ---------- CONTEXT ----------
interface FeatureContextType {
  canUse: (featureId: string) => boolean | string;
  loading: boolean;
}

const FeatureContext = createContext<FeatureContextType>({
  canUse: () => false,
  loading: true,
});

// ---------- PROVIDER ----------
export const FeatureProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { me }: any = useSelector((state: any) => state.me);
  const plan = (me?.plans?.toLowerCase() || "basic") as PlanType;
  const customFeatures = me?.customFeatures;

  const { data, loading } = useQuery(GET_PRODUCT_FEATURES);

  const featureMap = useMemo(() => {
    // 🚀 Custom Enterprise Logic:
    // If user is 'enterprise' AND has customFeatures set, use that JSON instead.
    if (plan === "enterprise" && customFeatures) {
      try {
        const parsed =
          typeof customFeatures === "string"
            ? JSON.parse(customFeatures)
            : customFeatures;
        return buildFeatureMap(parsed);
      } catch (err) {
        console.error("Custom feature parsing failed", err);
      }
    }

    // Default Logic:
    if (!data?.getProductFeatures) return {};
    try {
      const parsed =
        typeof data.getProductFeatures === "string"
          ? JSON.parse(data.getProductFeatures)
          : data.getProductFeatures;
      return buildFeatureMap(parsed);
    } catch (err) {
      console.error("Feature parsing failed", err);
      return {};
    }
  }, [data, plan, customFeatures]);

  const canUse = (featureId: string) =>
    canUseFeature(featureMap, plan, featureId);

  const value = useMemo(() => ({ canUse, loading }), [canUse, loading]);

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
};

// ---------- HOOK ----------
export const useFeature = () => useContext(FeatureContext);

export { TourProvider, useTour } from "./TourContext";
